import { createSignal, Show, For, createEffect, onCleanup, type Component, splitProps } from "solid-js";
import { platformConfig, resourcesConfig, detailedUseCasesConfig, staticLinks } from "../navbarConfig";
import { PlatformDropdownContent } from "./PlatformDropdown";
import { ResourcesDropdownContent } from "./ResourcesDropdown";
import { UseCasesDropdownContent } from "./UseCasesDropdown";
import { MobileNavbarMenu } from "../mobile/MobileNavbarMenu";
import { classNames } from "@core/lib/classNames";
import styles from "./Navbar.module.css";
import { HoverCard } from "@kobalte/core";
import { StIcon } from "@core/components/icons";
import { TbChevronDown, TbX, TbMenu2 } from "solid-icons/tb";
import { StorytellLogo } from "@core/components/StorytellLogo";
import { createBreakpoints } from "@solid-primitives/media";
import { A } from "@solidjs/router";
import { useWire } from "@core/wire";
import { UserAvatarWithMenu } from "@core/components/_original/UserAvatarWithMenu";
import { urls } from "@core/lib/urls";
import { StButton } from "@core/components/_original/StButton";

// Helper function to manage body scroll lock
const useBodyScrollLock = (locked: () => boolean) => {
  createEffect(() => {
    if (typeof document === "undefined") return;
    const originalStyle = document.body.style.overflow;
    if (locked()) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = originalStyle;
    }
    onCleanup(() => {
      document.body.style.overflow = originalStyle;
    });
  });
};

// Helper to create a unique ID for ARIA attributes
const createUniqueId = () => `navbar-dropdown-${Math.random().toString(36).substr(2, 9)}`;

interface NavItemProps {
  label: string;
  isActive?: boolean;
  isDropdown?: boolean;
  href?: string;
}

const NavItem = (props: NavItemProps) => {
  if (props.href) {
    return (
      <a href={props.href} class={styles["header-nav-link"]}>
        <span>{props.label}</span>
        <div class={styles["header-nav-link-underline"]} />
      </a>
    );
  }

  return (
    <button type="button" class={styles["header-nav-link"]}>
      {props.label}
      <Show when={props.isDropdown}>
        <span class={classNames(styles["header-nav-link-icon"], props.isActive ? "transform rotate-180" : "")}>
          <StIcon icon={TbChevronDown} size={20} />
        </span>
      </Show>
      <div class={styles["header-nav-link-underline"]} />
    </button>
  );
};

// Banner component with Chrome extension CTA
const NavbarBanner: Component<{
  show: boolean;
  onClose: () => void;
  ref?: HTMLDivElement | ((el: HTMLDivElement) => void);
}> = (props) => {
  // Splitting props to pass ref correctly
  const [local, others] = splitProps(props, ["show", "onClose", "ref"]);
  return (
    <Show when={local.show}>
      <div class="bg-indigo-800 text-white relative" ref={local.ref}>
        <div class="mx-auto max-w-screen-xl px-10 py-3 text-base leading-normal text-center">
          Bring the power of Storytell to every website! Get Our Chrome Extension.{" "}
          <a
            href="https://go.storytell.ai/chrome"
            target="_blank"
            rel="noreferrer"
            class="underline underline-offset-2"
          >
            Install it here.
          </a>
        </div>
        <StButton
          label="Close"
          simple
          class="min-w-max min-h-max absolute right-2 top-2 text-white hover:bg-white/20"
          icon={TbX}
          onClick={local.onClose}
        />
      </div>
    </Show>
  );
};

export const Navbar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = createSignal(false);
  const [showBanner, setShowBanner] = createSignal(true);
  const [bannerHeight, setBannerHeight] = createSignal(0);
  let bannerRef: HTMLDivElement | undefined;
  const wire = useWire();

  useBodyScrollLock(isMobileMenuOpen);

  const breakpoints = createBreakpoints({ desktop: "994px" }, { fallbackState: { desktop: false, key: "desktop" } });

  createEffect(() => {
    if (showBanner() && bannerRef) {
      setBannerHeight(bannerRef.offsetHeight);
    } else {
      setBannerHeight(0);
    }
  });

  const topOffsetForMenu = () => `calc(4.5rem + ${bannerHeight()}px)`;

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen());
  };

  let headerRef: HTMLElement | undefined;

  return (
    <Show
      when={breakpoints.desktop}
      fallback={
        <>
          <header class={classNames("dark", styles.header, styles.mobileHeaderFixed)}>
            <NavbarBanner ref={bannerRef} show={showBanner()} onClose={() => setShowBanner(false)} />
            <nav class={classNames(styles["header-nav"], styles.mobileNavContainerFirstChild)}>
              <div class={classNames(styles["header-nav-container"], styles.mobileNavInnerContainer)}>
                <StorytellLogo />

                <div class="flex items-center gap-3">
                  <Show when={wire.services.auth.isVerified()}>
                    <div class="inline-block w-auto h-auto">
                      <UserAvatarWithMenu />
                    </div>
                  </Show>

                  <button
                    type="button"
                    class={classNames(
                      "inline-flex items-center justify-center p-2 rounded-md",
                      "text-gray-400 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white",
                      styles.mobileMenuToggleButton,
                    )}
                    aria-controls="mobile-full-menu"
                    aria-expanded={isMobileMenuOpen()}
                    onClick={toggleMobileMenu}
                  >
                    <span class="sr-only">{isMobileMenuOpen() ? "Close main menu" : "Open main menu"}</span>
                    <Show when={!isMobileMenuOpen()} fallback={<StIcon icon={TbX} class="block h-6 w-6" />}>
                      <StIcon icon={TbMenu2} class="block h-6 w-6" />
                    </Show>
                  </button>
                </div>
              </div>
            </nav>
          </header>
          <Show when={isMobileMenuOpen()}>
            <MobileNavbarMenu
              isOpen={isMobileMenuOpen}
              closeMenu={() => setIsMobileMenuOpen(false)}
              platformConfig={platformConfig}
              resourcesConfig={resourcesConfig}
              useCasesConfig={detailedUseCasesConfig}
              staticLinks={staticLinks}
              topOffset={topOffsetForMenu}
            />
          </Show>
        </>
      }
    >
      <header class={classNames("dark", styles.header)} ref={headerRef}>
        <NavbarBanner ref={bannerRef} show={showBanner()} onClose={() => setShowBanner(false)} />
        <nav class={styles["header-nav"]}>
          <div class={styles["header-nav-container"]}>
            <StorytellLogo />
            <ul class={styles["header-nav-links"]}>
              <div class={styles["header-nav-links-left"]}>
                <li>
                  <HoverCard.Root
                    placement="bottom-start"
                    openDelay={100}
                    closeDelay={200}
                    ignoreSafeArea={true}
                    shift={-100}
                  >
                    <HoverCard.Trigger as="button">
                      <span class={styles["header-nav-link"]}>
                        <span>Platform</span>
                        <span class={styles["header-nav-link-icon"]}>
                          <StIcon icon={TbChevronDown} size={20} />
                        </span>
                        <div class={styles["header-nav-link-underline"]} />
                      </span>
                    </HoverCard.Trigger>
                    <HoverCard.Portal mount={headerRef}>
                      <HoverCard.Content class={classNames("dark", styles["header-hover-card"])}>
                        <PlatformDropdownContent config={platformConfig} />
                      </HoverCard.Content>
                    </HoverCard.Portal>
                  </HoverCard.Root>
                </li>

                <li>
                  <HoverCard.Root
                    placement="bottom-start"
                    openDelay={100}
                    closeDelay={200}
                    ignoreSafeArea={true}
                    shift={-100}
                  >
                    <HoverCard.Trigger as="button">
                      <span class={styles["header-nav-link"]}>
                        <span>Use Cases</span>
                        <span class={styles["header-nav-link-icon"]}>
                          <StIcon icon={TbChevronDown} size={20} />
                        </span>
                        <div class={styles["header-nav-link-underline"]} />
                      </span>
                    </HoverCard.Trigger>
                    <HoverCard.Portal mount={headerRef}>
                      <HoverCard.Content class={classNames("dark", styles["header-hover-card"])}>
                        <UseCasesDropdownContent />
                      </HoverCard.Content>
                    </HoverCard.Portal>
                  </HoverCard.Root>
                </li>

                <li>
                  <HoverCard.Root placement="bottom" openDelay={100} closeDelay={200} ignoreSafeArea={true}>
                    <HoverCard.Trigger as="span">
                      <a href="/" class={styles["header-nav-link"]}>
                        <span>Resources</span>
                        <span class={styles["header-nav-link-icon"]}>
                          <StIcon icon={TbChevronDown} size={20} />
                        </span>
                        <div class={styles["header-nav-link-underline"]} />
                      </a>
                    </HoverCard.Trigger>
                    <HoverCard.Portal mount={headerRef}>
                      <HoverCard.Content class={classNames("dark", styles["header-hover-card-resources"])}>
                        <ResourcesDropdownContent config={resourcesConfig} />
                      </HoverCard.Content>
                    </HoverCard.Portal>
                  </HoverCard.Root>
                </li>

                <For each={staticLinks}>
                  {(link) => (
                    <li>
                      <NavItem label={link.label} href={link.href} />
                    </li>
                  )}
                </For>
              </div>

              <div class={styles["header-nav-links-right"]}>
                <Show
                  when={wire.services.auth.isVerified()}
                  fallback={
                    <>
                      <li>
                        <A href="/auth/login" class={styles["header-nav-auth-login"]}>
                          Login
                        </A>
                      </li>
                      <li>
                        <A href="/auth/signup" class={styles["header-nav-auth-signup"]}>
                          Signup
                        </A>
                      </li>
                    </>
                  }
                >
                  <Show when={wire.services.collections.getRootsLoaded()}>
                    <A
                      href={urls.collection(wire.services.collections.getPersonalRoot()?.id ?? "")}
                      class="text-sm font-semibold leading-6 text-gray-100 underline underline-offset-2 mr-2"
                    >
                      Dashboard
                    </A>
                  </Show>
                  <div class="inline-block w-auto h-auto">
                    <UserAvatarWithMenu />
                  </div>
                </Show>
              </div>
            </ul>
          </div>
          <div class={styles["header-nav-underline"]} />
        </nav>
      </header>
    </Show>
  );
};
